import React from 'react';
import puno from './images/puno56.png';
import heroe from './images/heroe256.png';
import villano from './images/villano256.png';
import over from './images/over256.png';

// Reproductor de los combates de la arena, portado del prototipo (Arena Forallia v0) solo para los
// puños. No decide nada: anima los events que manda el servidor (apps/arena) y, al repetir, vuelve
// a animar los mismos.

// Poses del puño, girando desde el hombro (que queda dentro del cuerpo): guardia, carga y directo.
const PUNOS = {
    guard: 'rotate(-12deg) translate(0%, 0%)', wind: 'rotate(-28deg) translate(-18%, 0%)', wMs: 130,
    strike: 'rotate(0deg) translate(60%, -4%)', sMs: 80
};
const STAR = (c) => `<polygon points="52,26 57,38 70,34 61,45 72,54 58,53 56,66 50,55 38,62 44,49 32,42 46,40" fill="${c}" stroke="#fff" stroke-width="2"/>`;
const FACCION_IMG = { heroe, villano, neutro: over };
// Sonidos del combate, en public/audio/arena/<nombre>.mp3. Si el navegador bloquea el audio, se sigue sin él.
const sonar = (nombre) => new Audio(`/audio/arena/${nombre}.mp3`).play().catch(() => { });

// ¿La imagen no tiene fondo (más de un 2 % transparente)? Para leer sus píxeles se carga aparte con
// CORS; si su servidor no lo permite, cuenta como con fondo. Una vez por url (`visto` guarda lo ya
// resuelto para no pintar el marco un instante cada vez que vuelve a salir).
const sinFondo = new Map(), visto = new Map();
function mirarFondo(url) {
    if (!sinFondo.has(url)) {
        sinFondo.set(url, new Promise(resolve => {
            const im = new Image();
            im.crossOrigin = 'anonymous';
            im.onload = () => {
                try {
                    const n = 32, c = document.createElement('canvas');
                    c.width = c.height = n;
                    const g = c.getContext('2d');
                    g.drawImage(im, 0, 0, n, n);
                    const d = g.getImageData(0, 0, n, n).data;
                    let claros = 0;
                    for (let i = 3; i < d.length; i += 4) if (d[i] < 20) claros++;
                    resolve(claros / (n * n) > 0.02);
                } catch (e) {
                    resolve(false);
                }
            };
            im.onerror = () => resolve(false);
            im.src = url;
        }).then(v => { visto.set(url, v); return v; }));
    }
    return sinFondo.get(url);
}

/** El avatar con el mismo marco que en Mi cuenta (accountImageBorder); si no tiene fondo, la imagen
 *  sola, sin marco. Sin avatar, la imagen de su facción, también como en Mi cuenta. */
export function AvatarCuenta({ f }) {
    const [roto, setRoto] = React.useState(false);
    const [sinMarco, setSinMarco] = React.useState(() => !!visto.get(f.avatarUrl));
    React.useEffect(() => {
        let vivo = true;
        if (f.avatarUrl) mirarFondo(f.avatarUrl).then(v => { if (vivo) setSinMarco(v); });
        return () => { vivo = false; };
    }, [f.avatarUrl]);
    if (!f.avatarUrl || roto) return <img className='arenaSinAvatar' src={FACCION_IMG[f.faction] || over} alt='' />;
    if (sinMarco) return <img className='arenaSinAvatar' src={f.avatarUrl} alt='' onError={() => setRoto(true)} />;
    return (
        <div className='accountImageBorder' data-faccion={f.faction}>
            <img src={f.avatarUrl} alt='' onError={() => setRoto(true)} />
        </div>
    );
}

/** El avatar es el cuerpo: flota sin pies y lleva el puño delante. */
function Luchador({ f, flip }) {
    const body = (
        <div className='arenaLuchador'>
            <div className='bob'>
                {/* El avatar no se espeja nunca (las letras se leerían al revés): solo el puño. */}
                <div className='cuerpo' style={flip ? { transform: 'scaleX(-1)' } : undefined}><AvatarCuenta f={f} /></div>
                <div className='arm-f' style={{ transform: PUNOS.guard }}><img className='puno' data-faccion={f.faction} src={puno} alt='' /></div>
            </div>
            <div className='sombra'></div>
        </div>
    );
    return flip ? <div className='flipx'>{body}</div> : body;
}

// ---------- Reproducción (imperativa, como en el prototipo) ----------
function reproducir(el, combate, ctl, alAcabar) {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const token = (ctl.token = {});
    const max = combate.events[0].max;
    let hp = combate.events[0].hp.slice();

    const wait = (ms) => new Promise(r => setTimeout(r, reduced ? ms * 0.5 : ms));
    const anim = (node, kf, ms) => (reduced ? Promise.resolve() : node.animate(kf, { duration: ms, easing: 'ease-out' }).finished.catch(() => { }));

    // Anima de la pose actual a la nueva y la deja fijada
    function pose(node, to, ms, easing = 'ease-in-out') {
        const from = node._t || node.style.transform || 'rotate(0deg)';
        node._t = to; node.style.transform = to;
        const old = node._a; node._a = null;
        if (reduced) { if (old) old.cancel(); return Promise.resolve(); }
        const a = node.animate([{ transform: from }, { transform: to }], { duration: ms, easing });
        if (old) old.cancel();
        node._a = a;
        return a.finished.catch(() => { });
    }
    // Estrella de impacto sobre el rival
    function estrella(a, d, h) {
        if (reduced) return;
        const s = el.stage.getBoundingClientRect(), f = el.ftr[d].getBoundingClientRect();
        const fx = document.createElement('div');
        fx.className = 'fx';
        Object.assign(fx.style, { left: f.left - s.left + 'px', top: f.top - s.top + f.height * 0.05 + 'px', width: f.width + 'px', height: f.height * 0.75 + 'px' });
        const g = STAR(h.crit ? '#ffd700' : '#7fffd4');
        fx.innerHTML = `<svg viewBox="0 0 100 100" preserveAspectRatio="none" fill="none">${a === 1 ? `<g transform="translate(100 0) scale(-1 1)">${g}</g>` : g}</svg>`;
        fx.querySelector('polygon').setAttribute('vector-effect', 'non-scaling-stroke');
        el.stage.appendChild(fx);
        const op = h.miss ? 0.4 : 1, sc = h.crit ? 1.15 : 1;
        fx.animate([{ opacity: 0, transform: `scale(${sc * 0.9})` }, { opacity: op, transform: `scale(${sc})`, offset: 0.15 }, { opacity: op, offset: 0.55 }, { opacity: 0, transform: `scale(${sc})` }],
            { duration: 440, easing: 'ease-out' }).finished.then(() => fx.remove()).catch(() => fx.remove());
    }
    function shake(px) {
        if (reduced || !px) return;
        el.stage.animate([{ transform: 'none' }, { transform: `translate(${px}px,${-px / 2}px)` }, { transform: `translate(${-px}px,${px / 2}px)` }, { transform: `translate(${px / 2}px,0)` }, { transform: 'none' }], { duration: 280 });
    }
    // Sin pies: al moverse avanza flotando y balanceándose de lado a lado
    function flotar(i) {
        if (reduced) return () => { };
        const a = el.ftr[i].querySelector('.bob').animate([{ transform: 'translateY(0) rotate(0deg)' }, { transform: 'translateY(-5%) rotate(5deg)' }, { transform: 'translateY(0) rotate(0deg)' }, { transform: 'translateY(-5%) rotate(-5deg)' }, { transform: 'translateY(0) rotate(0deg)' }],
            { duration: 320, iterations: Infinity });
        return () => a.cancel();
    }
    function setX(i, x, ms) {
        el.ftr[i].style.transition = reduced ? 'none' : `transform ${ms}ms ease-in-out`;
        el.ftr[i].style.transform = x ? `translateX(${x}px)` : '';
        return wait(ms);
    }
    function gap() {
        const a = el.ftr[0].getBoundingClientRect(), b = el.ftr[1].getBoundingClientRect();
        return Math.max(0, b.left - a.right) + a.width * 0.25;
    }
    function floatTxt(i, text, cls) {
        const s = el.stage.getBoundingClientRect(), f = el.ftr[i].getBoundingClientRect();
        const t = document.createElement('div');
        t.className = 'float ' + cls; t.textContent = text;
        t.style.left = f.left - s.left + f.width / 2 + 'px'; t.style.top = f.top - s.top + f.height * 0.05 + 'px';
        el.stage.appendChild(t);
        const half = t.offsetWidth / 2, x = parseFloat(t.style.left);
        t.style.left = Math.min(Math.max(x, half + 6), s.width - half - 6) + 'px';
        const kf = reduced
            ? [{ transform: 'translate(-50%,-6px)', opacity: 0 }, { transform: 'translate(-50%,-6px)', opacity: 1, offset: 0.2 }, { transform: 'translate(-50%,-6px)', opacity: 0 }]
            : [{ transform: 'translate(-50%,8px)', opacity: 0 }, { transform: 'translate(-50%,-6px)', opacity: 1, offset: 0.2 }, { transform: 'translate(-50%,-46px)', opacity: 0 }];
        t.animate(kf, { duration: reduced ? 900 : 1000, easing: 'ease-out' }).finished.then(() => t.remove()).catch(() => t.remove());
    }
    function setHp(v) {
        for (const i of [0, 1]) {
            const pct = (v[i] / max[i]) * 100;
            el.bar[i].style.width = pct + '%';
            el.bar[i].className = pct <= 25 ? 'crit' : pct <= 50 ? 'lo' : '';
            el.num[i].textContent = `${v[i]} / ${max[i]}`;
        }
    }
    async function hitFx(d, h) {
        const inner = el.ftr[d].firstElementChild, dir = d === 0 ? -1 : 1;
        if (h.miss) {
            floatTxt(d, 'ESQUIVA', 'miss');
            await anim(inner, [{ transform: 'none' }, { transform: `translate(${dir * 22}px,-10px)` }, { transform: 'none' }], 320);
        } else {
            floatTxt(d, (h.crit ? '¡CRÍTICO! ' : '') + '-' + h.dmg, h.crit ? 'crit' : 'dmg');
            await anim(inner, [{ transform: 'none', filter: 'none' }, { transform: `translateX(${dir * 10}px) rotate(${dir * 6}deg)`, filter: 'brightness(1.9) saturate(.3)' }, { transform: `translateX(${dir * -4}px)`, filter: 'none' }, { transform: 'none', filter: 'none' }], h.crit ? 380 : 260);
        }
    }
    // Acercarse y cargar → directo → estrella y reacción del rival
    async function attackAnim(a, d, hits, quick) {
        const k = quick ? 0.7 : 1;
        const arm = el.ftr[a].querySelector('.arm-f'), body = el.ftr[a].firstElementChild;
        const dist = gap() * (quick ? 0.8 : 1) * (a === 0 ? 1 : -1);
        const stop = flotar(a);
        await Promise.all([setX(a, dist, 300 * k), pose(arm, PUNOS.wind, PUNOS.wMs * k), pose(body, 'rotate(-5deg)', PUNOS.wMs * k)]);
        stop();
        for (let i = 0; i < hits.length; i++) {
            const h = hits[i];
            pose(body, 'rotate(6deg)', PUNOS.sMs * k);
            await pose(arm, PUNOS.strike, PUNOS.sMs * k, 'cubic-bezier(.2,.9,.3,1)');
            estrella(a, d, h);
            if (!h.miss) { sonar('golpe'); shake(h.crit ? 4 : 0); hp[d] = Math.max(0, hp[d] - h.dmg); setHp(hp); }
            await hitFx(d, h);
            if (i < hits.length - 1) await pose(arm, PUNOS.wind, PUNOS.wMs * 0.5 * k);
        }
    }
    async function recover(a, quick) {
        const k = quick ? 0.7 : 1, stop = flotar(a);
        await Promise.all([pose(el.ftr[a].querySelector('.arm-f'), PUNOS.guard, 220 * k), pose(el.ftr[a].firstElementChild, 'rotate(0deg)', 220 * k), setX(a, 0, 280 * k)]);
        stop();
    }
    async function play(ev) {
        // Con puños no hay aturdidos: solo llegan ataques y contraataques.
        if (ev.type !== 'attack' && ev.type !== 'counter') return;
        const a = ev.who, counter = ev.type === 'counter';
        if (counter) floatTxt(a, '¡CONTRAATAQUE!', 'counter');
        await attackAnim(a, 1 - a, counter ? [ev.hit] : ev.hits, counter);
        hp = ev.hp.slice(); setHp(hp);
        await recover(a, counter);
        await wait(160);
    }
    function finish() {
        const end = combate.events[combate.events.length - 1], l = 1 - end.winner;
        setHp(end.hp);
        for (const i of [0, 1]) {
            el.ftr[i].style.transition = 'none'; el.ftr[i].style.transform = '';
            el.ftr[i].querySelector('.bob').classList.add('still');
            const arm = el.ftr[i].querySelector('.arm-f');
            if (arm._a) arm._a.cancel();
            arm.style.transform = PUNOS.guard; arm._t = null;
        }
        // KO: el que pierde cae de lado
        const lo = el.ftr[l].firstElementChild;
        if (lo._a) lo._a.cancel();
        lo.style.transition = reduced ? 'none' : 'transform .5s ease-in';
        lo.style.transform = `translateY(8%) rotate(${l === 0 ? -82 : 82}deg)`;
    }

    // Todo a cero (también al repetir)
    el.stage.querySelectorAll('.float,.fx').forEach(n => n.remove());
    for (const i of [0, 1]) {
        const inner = el.ftr[i].firstElementChild, arm = el.ftr[i].querySelector('.arm-f');
        el.ftr[i].style.transition = 'none'; el.ftr[i].style.transform = '';
        el.ftr[i].querySelector('.bob').classList.remove('still');
        if (inner._a) inner._a.cancel();
        inner._t = null; inner.style.transition = 'none'; inner.style.transform = '';
        if (arm._a) arm._a.cancel();
        arm._t = null; arm.style.transform = PUNOS.guard;
    }
    setHp(hp);

    (async () => {
        // Entrada: los dos llegan flotando desde los lados
        if (!reduced) {
            const off = el.stage.getBoundingClientRect().width * 0.32;
            for (const i of [0, 1]) el.ftr[i].style.transform = `translateX(${i ? off : -off}px)`;
            void el.stage.offsetWidth;
            const stops = [0, 1].map(flotar);
            await Promise.all([setX(0, 0, 750), setX(1, 0, 750)]);
            stops.forEach(f => f());
        }
        await wait(250);
        for (const ev of combate.events) {
            if (ctl.token !== token) return;
            if (ev.type === 'end') break;
            await play(ev);
        }
        if (ctl.token !== token) return;
        finish();
        alAcabar();
    })();
}

/** combate = { fighters, events, winner, reason, yo } tal y como lo manda el servidor. `children`
 *  (el premio) sale al acabar, para no destripar el resultado. */
const ArenaCombate = ({ combate, onLucharDeNuevo, onCerrar, children }) => {
    const root = React.useRef(null);
    const ctl = React.useRef({ token: null });
    const [fin, setFin] = React.useState(false);
    const [vez, setVez] = React.useState(0);

    React.useEffect(() => {
        const c = ctl.current, r = root.current;
        const el = { stage: r, ftr: [...r.querySelectorAll('.ftr')], bar: [...r.querySelectorAll('.hp .bar i')], num: [...r.querySelectorAll('.hp .num')] };
        reproducir(el, combate, c, () => setFin(true));
        return () => { c.token = null; };
    }, [combate, vez]);

    const ganaste = combate.winner === combate.yo;
    return (
        <>
            <div className='arenaStage' ref={root}>
                {combate.fighters.map((f, i) => (
                    <div key={'hp' + i} className={'hp ' + (i ? 'r' : 'l')}>
                        <span className='nm'><span className={'f-' + f.faction}>{f.name}</span> <small>NV {f.level}</small></span>
                        <div className='bar'><i /></div>
                        <span className='num'></span>
                    </div>
                ))}
                {combate.fighters.map((f, i) => (
                    <div key={'f' + i} className={'ftr ' + (i ? 'r' : 'l')}>
                        <div className='inner'><Luchador f={f} flip={i === 1} /></div>
                    </div>
                ))}
                {fin ? (
                    <div className='banner'>
                        <h3 className={ganaste ? 'win' : 'lose'}>{ganaste ? '¡VICTORIA!' : 'DERROTA'}</h3>
                        <p>{combate.reason === 'ko' ? 'Por KO' : 'A los puntos'}</p>
                    </div>
                ) : null}
            </div>
            {/* Oculto y no desmontado: al Repetir conserva lo ya elegido (premio, mejora) */}
            <div hidden={!fin}>{children}</div>
            {fin ? (
                <div className='arenaAcciones'>
                    <button type='button' className='btnForallia arenaBtn' onClick={onLucharDeNuevo}>Luchar de nuevo</button>
                    <button type='button' className='btnForallia arenaBtn' onClick={() => { setFin(false); setVez(v => v + 1); }}>Repetir</button>
                    <button type='button' className='btnForallia arenaBtn' onClick={onCerrar}>Cerrar</button>
                </div>
            ) : null}
        </>
    );
};

export default ArenaCombate;
